/*
  Cart drawer behaviour.

  Kept as a normal theme asset rather than a section `javascript` block: those
  are compiled into a shared bundle whose URL version does not rotate when the
  source changes, so edits can sit behind a stale CDN copy for a long time.
  A regular asset URL is versioned per file and updates immediately. It also
  means Liquid values must arrive through data attributes, which is why the
  fee handles and fee quantity are read off the drawer element.
*/
(() => {
  if (window.bjafCartDrawerLoaded) {
    return;
  }

  window.bjafCartDrawerLoaded = true;

  /* Fee lines are billed but never counted as items, mirroring the Liquid
     tally. The theme's own `cart-count` element listens for cart:change and
     writes the raw count, so the subtraction is patched into its setter - the
     one choke point every writer goes through - rather than racing it and
     letting the badge disagree with the drawer heading. */
  const drawerElement = document.querySelector('.cart-drawer');

  const feeHandles = (drawerElement?.dataset.bjafFeeHandles || '')
    .split(',')
    .filter(Boolean);

  let feeQuantity =
    Number.parseInt(drawerElement?.dataset.bjafFeeQuantity || '0', 10) || 0;

  const readFeeQuantity = (cart) => {
    if (!Array.isArray(cart?.items) || feeHandles.length === 0) {
      return feeQuantity;
    }

    return cart.items.reduce(
      (total, item) =>
        feeHandles.includes(item.handle) ? total + item.quantity : total,
      0
    );
  };

  /* cart:change bubbles up from documentElement, so this capture-phase
     listener always lands before any bubble-phase one, including the theme's. */
  document.addEventListener(
    'cart:change',
    (event) => {
      feeQuantity = readFeeQuantity(event.detail?.cart);
    },
    true
  );

  customElements.whenDefined('cart-count').then(() => {
    const prototype = customElements.get('cart-count').prototype;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'itemCount');

    if (!descriptor?.set || prototype.bjafExcludesFees) {
      return;
    }

    Object.defineProperty(prototype, 'itemCount', {
      configurable: true,
      get: descriptor.get,
      set(count) {
        descriptor.set.call(this, Math.max(count - feeQuantity, 0));
      },
    });

    prototype.bjafExcludesFees = true;
  });

  /* The drawer header lives in the `header` slot, outside `.cart-drawer__inner`
     - the only region the theme's cart JS re-renders on `cart:change` - so the
     "N items" string has to be updated here or it goes stale (and would show
     e.g. "1 items" after a quantity change). */
  const syncCartHeaderCount = (cart) => {
    if (typeof cart?.item_count !== 'number') {
      return;
    }

    const productCount = Math.max(cart.item_count - readFeeQuantity(cart), 0);
    const countElement = document.querySelector(
      '.cart-drawer [data-bjaf-cart-count]'
    );

    if (countElement) {
      const suffix =
        productCount === 1
          ? countElement.dataset.labelOne
          : countElement.dataset.labelOther;

      countElement.textContent = `${productCount} ${suffix}`;
    }

    /* Impact's cart-count listener occasionally misses product-form additions
       from inside this re-rendering drawer. Drive the public header badge from
       the same cart payload so it never remains one item behind. The raw count
       goes in because the patched setter does the fee subtraction. */
    document.querySelectorAll('cart-count').forEach((cartCount) => {
      cartCount.itemCount = cart.item_count;
    });
  };

  /* The cart drawer slides in from the side with the overlay fading, on phones and desktop (2026-10-22).
     Reduced motion keeps the short clip reveal.
     Also kill Impact's 1250ms delayed innerHTML swap on cart:change so line
     items update immediately instead of fading in late. */
  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype._bjafSnappyPatched) {
      return;
    }

    CartDrawer.prototype._bjafSnappyPatched = true;

    const sectionIdFrom = (element) => {
      const section = element.closest('.shopify-section');
      return section ? section.id.replace('shopify-section-', '') : '';
    };

    /* v5: the whole drawer (header, body and footer) is re-rendered from the section HTML, because the
       free-shipping bar and item count live in the header and the totals in the footer. */
    CartDrawer.prototype._onCartChanged = async function (event) {
      const sectionHtml =
        event.detail.cart['sections'] && event.detail.cart['sections'][sectionIdFrom(this)];
      if (!sectionHtml) {
        return;
      }

      const updated = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('.cart-drawer');
      if (!updated) {
        return;
      }

      this.replaceChildren(...updated.childNodes);
      this.classList.remove('is-busy');
    };

    /* Slide from the side with the overlay fading in (phones and desktop); reduced motion keeps the short reveal. */
    const slides = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offscreen = () => (document.documentElement.dir === 'rtl' ? 'translateX(-100%)' : 'translateX(100%)');
    const afterTransition = (element, ms) =>
      new Promise((resolve) => {
        const done = (event) => {
          if (event && event.target !== element) {
            return;
          }
          element.removeEventListener('transitionend', done);
          resolve();
        };
        element.addEventListener('transitionend', done);
        window.setTimeout(done, ms);
      });

    CartDrawer.prototype._showTransition = function (animate19 = true) {
      this._setInitialPosition();
      const content = this.shadowRoot?.querySelector('[part="content"]');
      const overlay = this.shadowRoot?.querySelector('[part="overlay"]');
      const closeButton = this.shadowRoot?.querySelector(
        '[part="outside-close-button"]'
      );

      if (!content) {
        this.style.opacity = '1';
        this.style.visibility = 'visible';
        return Promise.resolve();
      }

      this.style.opacity = '1';
      this.style.visibility = 'visible';
      Array.from(content.children).forEach((child) => {
        child.style.opacity = '1';
      });

      if (animate19 && slides()) {
        content.style.transition = 'none';
        content.style.clipPath = 'none';
        content.style.transform = offscreen();
        if (overlay) {
          overlay.style.transition = 'none';
          overlay.style.opacity = '0';
        }
        void content.offsetWidth;
        content.style.transition = 'transform 0.34s cubic-bezier(0.32, 0.72, 0, 1)';
        content.style.transform = 'translateX(0)';
        if (overlay) {
          overlay.style.transition = 'opacity 0.3s ease';
          overlay.style.opacity = '1';
        }
        return afterTransition(content, 420).then(() => {
          this.classList.add('show-close-cursor');
        });
      }

      content.style.transform = '';
      if (overlay) {
        overlay.style.transition = '';
        overlay.style.opacity = '';
      }
      content.style.transition = animate19
        ? 'clip-path 0.16s cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
      content.style.clipPath = this._getClipPathProperties()[0];
      if (closeButton) {
        closeButton.style.opacity = '1';
      }

      void content.offsetWidth;
      content.style.clipPath = this._getClipPathProperties()[1];

      return new Promise((resolve) => {
        const done = () => {
          content.removeEventListener('transitionend', done);
          this.classList.add('show-close-cursor');
          resolve();
        };
        if (!animate19) {
          done();
          return;
        }
        content.addEventListener('transitionend', done);
        window.setTimeout(done, 220);
      });
    };

    CartDrawer.prototype._hideTransition = function () {
      const content = this.shadowRoot?.querySelector('[part="content"]');
      const overlay = this.shadowRoot?.querySelector('[part="overlay"]');
      if (!content) {
        this.style.opacity = '0';
        this.style.visibility = 'hidden';
        return Promise.resolve();
      }

      if (slides()) {
        content.style.transition = 'transform 0.26s cubic-bezier(0.4, 0, 1, 1)';
        content.style.transform = offscreen();
        if (overlay) {
          overlay.style.transition = 'opacity 0.26s ease';
          overlay.style.opacity = '0';
        }
        return afterTransition(content, 340).then(() => {
          this.style.opacity = '0';
          this.style.visibility = 'hidden';
          this.classList.remove('show-close-cursor');
        });
      }

      content.style.transition = 'clip-path 0.14s cubic-bezier(0.22, 1, 0.36, 1)';
      content.style.clipPath = this._getClipPathProperties().reverse()[0];

      return new Promise((resolve) => {
        const done = () => {
          content.removeEventListener('transitionend', done);
          this.style.opacity = '0';
          this.style.visibility = 'hidden';
          this.classList.remove('show-close-cursor');
          resolve();
        };
        content.addEventListener('transitionend', done);
        window.setTimeout(done, 200);
      });
    };
  });

  /* Re-open drawer after /cart bounce (drawer-only mode). */
  document.addEventListener('DOMContentLoaded', () => {
    try {
      if (sessionStorage.getItem('bjaf-open-cart') !== '1') {
        return;
      }
      sessionStorage.removeItem('bjaf-open-cart');
    } catch (e) {
      return;
    }

    customElements.whenDefined('cart-drawer').then(() => {
      const drawer = document.querySelector('cart-drawer');
      drawer?.show?.(false);
    });
  });

  document.addEventListener('cart:change', (event) => {
    syncCartHeaderCount(event.detail?.cart);
  });

  /* ---------- v5 drawer actions ---------- */
  const root = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.querySelector('cart-drawer.bcd');
  const drawerSectionId = () => {
    const section = drawer()?.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  /* Impact binds its own cart:change handler when the drawer is first connected, which can be before this file
     runs, so the prototype patch above does not always take. This listener re-renders the whole drawer from the
     section HTML that comes with every cart change. */
  const renderDrawer = (cart) => {
    const element = drawer();
    const sectionId = drawerSectionId();
    const html = cart?.sections && sectionId ? cart.sections[sectionId] : null;
    if (!element || !html) {
      return;
    }
    const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer');
    if (updated) {
      element.replaceChildren(...updated.childNodes);
    }
    element.classList.remove('is-busy');
  };

  document.addEventListener('cart:change', (event) => renderDrawer(event.detail?.cart));

  const sendCartChange = (cart) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
    );
  };

  /* Runs a cart request and announces the new cart (with the drawer's re-rendered HTML) to the theme. */
  const cartRequest = async (path, body) => {
    const sectionId = drawerSectionId();
    drawer()?.classList.add('is-busy');
    try {
      const payload = { ...body };
      if (sectionId) {
        payload.sections = [sectionId];
        payload.sections_url = window.location.pathname;
      }
      const response = await fetch(root() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      let cart = data;
      if (typeof data.item_count !== 'number') {
        cart = await fetch(root() + 'cart.js').then((r) => r.json());
        cart.sections = data.sections;
      }
      sendCartChange(cart);
      return cart;
    } catch (error) {
      drawer()?.classList.remove('is-busy');
      throw error;
    }
  };

  /* Kaching keeps its quantity-break setup in the product page; it is read once per product and cached.
     The cart line then gets the same hidden property Kaching sets on the product page, so its discount applies. */
  const kachingDeals = new Map();
  const kachingDeal = (url) => {
    if (!kachingDeals.has(url)) {
      kachingDeals.set(
        url,
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const element = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('script.kaching-bundles-deal-block-settings');
            return element ? JSON.parse(element.textContent) : null;
          })
          .catch(() => null)
      );
    }
    return kachingDeals.get(url);
  };

  const randomId = () => Math.random().toString(36).slice(2, 6);

  const removeKeys = (keys) =>
    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: Object.fromEntries(keys.map((key) => [key, 0])) }),
    });

  /* Same as the product page: the product's lines (paid and Kaching's free line) are removed, then the product is
     added again with the chosen tier's quantity and Kaching property, and Kaching adds the free jar. */
  const switchTier = async (line, tierBox, tier) => {
    drawer()?.classList.add('is-busy');
    const quantity = tier === 2 ? 3 : 1;
    const deal = await kachingDeal(tierBox.dataset.url);
    const bars = deal?.dealBars || [];
    const bar =
      tier === 2
        ? bars.find((b) => Number(b.buyQuantity) === 2 && Number(b.getQuantity) === 1)
        : bars.find((b) => Number(b.quantity) === 1);
    const item = { id: Number(tierBox.dataset.variantId), quantity };
    if (deal && bar) {
      item.properties = {
        __kaching_bundles: JSON.stringify({
          deal: deal.nanoId,
          pp: Number(tierBox.dataset.productId),
          main: true,
          v: deal.versionId,
          id: randomId(),
          bid: bar.id,
        }),
      };
    }
    await removeKeys((line.dataset.keys || line.dataset.key).split(',').filter(Boolean));
    return cartRequest('cart/add.js', { items: [item] });
  };

  const applyDiscountCode = async (code) => {
    const current = (drawer()?.dataset.bcdDiscounts || '').split(',').filter(Boolean);
    if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
      return;
    }
    await cartRequest('cart/update.js', { discount: [...current, code].join(',') });
  };

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!target.closest || !target.closest('cart-drawer.bcd')) {
      return;
    }

    const qtyButton = target.closest('[data-bcd-qty]');
    if (qtyButton) {
      const line = qtyButton.closest('[data-bcd-line]');
      const quantity = Number(qtyButton.dataset.bcdQty);
      if (quantity === 0) {
        /* Removing a supplement also removes Kaching's free jar line. */
        const keys = (line.dataset.keys || line.dataset.key).split(',').filter(Boolean);
        cartRequest('cart/update.js', { updates: Object.fromEntries(keys.map((key) => [key, 0])) });
      } else {
        cartRequest('cart/change.js', { id: line.dataset.key, quantity });
      }
      return;
    }

    const tierButton = target.closest('[data-bcd-tier]');
    if (tierButton) {
      if (tierButton.classList.contains('is-on')) {
        return;
      }
      const line = tierButton.closest('[data-bcd-line]');
      const tierBox = tierButton.closest('[data-bcd-tiers]');
      switchTier(line, tierBox, Number(tierButton.dataset.bcdTier));
      return;
    }

    const addButton = target.closest('[data-bcd-add]');
    if (addButton) {
      cartRequest('cart/add.js', { items: [{ id: Number(addButton.dataset.bcdAdd), quantity: 1 }] });
      return;
    }

    const secretButton = target.closest('[data-bcd-secret-add]');
    if (secretButton) {
      const code = secretButton.closest('.bcd-secret')?.dataset.code;
      await cartRequest('cart/add.js', { items: [{ id: Number(secretButton.dataset.bcdSecretAdd), quantity: 1 }] });
      if (code) {
        applyDiscountCode(code);
      }
    }
  });

  /* Warm the Kaching setup for supplement lines as soon as the drawer opens, so a tier switch is quick. */
  document.addEventListener('dialog:after-show', (event) => {
    if (!event.target.matches?.('cart-drawer.bcd')) {
      return;
    }
    event.target.querySelectorAll('[data-bcd-tiers]').forEach((box) => kachingDeal(box.dataset.url));
  });
})();
